import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Confirmar } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { recuperarFoco } from '../../nucleo/foco';
import { documentoFormatado, nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CAMINHO_EMPRESAS, invalidarVinculos, PopupVincular, responsaveisDoRegistro, type ParceiroCrm } from './crm-comum';
import { AvisoListaCheia, AvisoPreservadas, comFilial, filialDaBusca, herancaDaMatriz, LIMITE_DA_LISTA, ListaDeFiliais, MAX_FILIAIS_DA_MATRIZ, usePerguntaDasFiliais, vincularFiliais, type ItemFilial } from './CrmFiliais';
import { FormEmpresa } from './CrmFormularios';
import type { Ficha } from './tipos';

/** Os cartões Filiais e Matriz da ficha. Fora de `CrmFiliais.tsx` porque usam o `FormEmpresa`, que usa o bloco de filiais de lá. */
type EmpresaDaFicha = Ficha & ParceiroCrm;

/**
 * Rodada rfil-2 (VIS-B3): sem Adicionar filial (no teto, ou empresa arquivada), o foco fica no cartão, e não vai ao `#conteudo`: a primeira
 * filial e, sem nenhuma, o próprio cartão.
 */
const RESERVAS_DO_FOCO = ['[data-abrir-adicionar-filial]', '[data-filial] a', '[data-cartao-filiais]'];

/**
 * O cartão Filiais da matriz: adicionar, desvincular e abrir. Filial não tem filiais, então não o mostra. Pedido de 04/11: o pop-up Adicionar
 * filial monta uma lista (busca e Cadastrar filial, que abre o `FormEmpresa` como Criar filial e volta) e o Vincular liga todas. A lista fica
 * aqui porque o pop-up fecha enquanto o formulário está aberto.
 */
export function CartaoFiliais({ e }: { e: EmpresaDaFicha }) {
  const { t } = useTranslation('cadastros');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [etapa, setEtapa] = useState<'fechado' | 'popup' | 'formulario'>('fechado');
  const [selecionadas, setSelecionadas] = useState<ItemFilial[]>([]);
  const desistir = usePerguntaDasFiliais(selecionadas, setSelecionadas);
  const [vinculando, setVinculando] = useState(false);
  const emCurso = useRef(false);
  const cartao = useRef<HTMLDivElement>(null);
  // Foco depois da troca de etapa: o efeito de quem chama roda depois do foco inicial do pop-up e da devolução do formulário.
  const focoDepois = useRef<(() => void) | null>(null);
  useEffect(() => {
    const f = focoDepois.current;
    focoDepois.current = null;
    f?.();
  }, [etapa]);
  const [tirar, setTirar] = useState<{ id: string; nome: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  // Rodada rcn-6 (ROB-B3): a ficha corta em 200 (a mescla pode passar do teto); "Ver todas" traz o resto da lista.
  const [todas, setTodas] = useState<{ id: string; nome: string; documento: string | null }[] | null>(null);
  const [carregandoTodas, setCarregandoTodas] = useState(false);
  // Rodada rfil-2 (VIS-B2): o botão some com a lista inteira; o foco vai à primeira linha nova, depois de ela existir.
  const focarLinha = useRef<number | null>(null);
  useEffect(() => {
    if (focarLinha.current === null) return;
    const i = focarLinha.current;
    focarLinha.current = null;
    recuperarFoco({ seletor: '[data-filial] a', i, reservas: RESERVAS_DO_FOCO });
  }, [todas]);
  const podeEditar = s.pode('cadastros.editar');
  if (e.matriz_id) return null;
  const lista = todas ?? e.filiais ?? [];
  const total = e.filiais_total ?? lista.length;
  // O teto da matriz e o tamanho da lista: a rota vincula uma de cada vez, e o servidor segue recusando o que passar do teto.
  const limite = Math.max(Math.min(LIMITE_DA_LISTA, MAX_FILIAIS_DA_MATRIZ - total), 0);
  const fecharPopup = () => {
    if (emCurso.current || desistir.aberta) return;
    desistir.perguntarOu(selecionadas, true, () => {
      setSelecionadas([]);
      setEtapa('fechado');
      recuperarFoco({ reservas: RESERVAS_DO_FOCO });
    }, true);
  };
  const vincular = async () => {
    if (emCurso.current || !selecionadas.length) return;
    emCurso.current = true;
    setVinculando(true);
    try {
      const falhas = await vincularFiliais(e.id, selecionadas);
      // Rodada rfil-5 (ROB-B2): sem esperar a releitura, que prendia o pop-up com tudo já vinculado.
      void invalidarVinculos(qc);
      const feitas = selecionadas.length - falhas.length;
      if (!falhas.length) {
        toast.sucesso(t('crm.filiais.vinculadas', { count: feitas }));
        setSelecionadas([]);
        desistir.limparAviso();
        focoDepois.current = () => cartao.current?.focus();
        setEtapa('fechado');
        return;
      }
      // Falha parcial: as vinculadas saem, as outras ficam com o motivo e o pop-up segue aberto.
      if (feitas) toast.sucesso(t('crm.filiais.vinculadas', { count: feitas }));
      const motivos = new Map(falhas.map((f) => [f.id, f.motivo]));
      setSelecionadas((x) => x.filter((f) => motivos.has(f.id)).map((f) => ({ ...f, motivo: motivos.get(f.id), motivo_exclusao: undefined })));
      window.setTimeout(() => document.querySelector<HTMLElement>(`[data-tirar-da-lista="${falhas[0]!.id}"]`)?.focus(), 0);
    } finally {
      emCurso.current = false;
      setVinculando(false);
    }
  };
  const verTodas = async () => {
    setCarregandoTodas(true);
    try {
      const itens = (await getTodos<{ id: string; nome: string; documento: string | null }>('/cadastros/parceiros', { papel: 'empresa', grupo_de: e.id })).itens;
      focarLinha.current = lista.length;
      setTodas(itens);
    } catch (err) {
      toast.erro(mensagemDeErro(err));
    } finally {
      setCarregandoTodas(false);
    }
  };
  const confirmarTirar = async () => {
    if (!tirar) return;
    setOcupado(true);
    const posicao = lista.findIndex((f) => f.id === tirar.id);
    try {
      await del(`/cadastros/parceiros/${e.id}/filiais/${tirar.id}`);
      setTirar(null);
      setTodas((x) => x?.filter((f) => f.id !== tirar.id) ?? null);
      await invalidarVinculos(qc);
      toast.sucesso(t('crm.filiais.desvinculada'));
      // Rodada rcn-6 (VIS-B2): o botão saiu com a linha; o foco vai à linha que ficou no lugar, senão a Adicionar filial.
      recuperarFoco({ seletor: '[data-desvincular-filial]', i: posicao, reservas: RESERVAS_DO_FOCO });
    } catch (err) {
      setTirar(null);
      toast.erro(mensagemDeErro(err));
    } finally {
      setOcupado(false);
    }
  };
  return (
    <>
      <div ref={cartao} tabIndex={-1} className="min-w-0 focus:outline-none" data-cartao-filiais="">
      <Cartao
        titulo={total ? `${t('crm.filiais.titulo')} (${total})` : t('crm.filiais.titulo')}
        acao={
          podeEditar &&
          !e.arquivado &&
          !e.filiais_no_teto && (
            <Botao tamanho="sm" variante="fantasma" icone={<Plus className="size-4" />} onClick={() => setEtapa('popup')} data-abrir-adicionar-filial="">
              {t('crm.filiais.adicionar')}
            </Botao>
          )
        }
      >
        {e.filiais_no_teto && podeEditar && !e.arquivado && <p className="mb-2 text-xs text-tinta-3" data-filiais-no-teto="">{t('crm.filiais.noTeto')}</p>}
        {lista.length ? (
          <ul className="flex flex-col" data-filiais-empresa="">
            {lista.map((f) => (
              <li key={f.id} className="flex min-h-11 items-center gap-2 border-b border-borda px-2 text-sm last:border-0" data-filial={f.id}>
                <span className="flex min-w-0 flex-1 flex-col">
                  <Link to={`${CAMINHO_EMPRESAS}/${f.id}`} className="truncate font-semibold text-azul hover:underline" title={nomePessoa(f.nome)}>
                    {nomePessoa(f.nome)}
                  </Link>
                  {f.documento && <span className="numero truncate text-xs text-tinta-3">{documentoFormatado(f.documento)}</span>}
                </span>
                {podeEditar && (
                  <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.filiais.desvincularDe', { nome: nomePessoa(f.nome) })} title={t('crm.filiais.desvincular')} icone={<X className="size-4" />} onClick={() => setTirar(f)} data-desvincular-filial={f.id} />
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-tinta-3">{t('crm.filiais.vazio')}</p>
        )}
        {total > lista.length && (
          <div className="mt-2 flex flex-col items-start gap-1" data-filiais-cortadas="">
            <p className="text-xs text-tinta-3">{t('crm.filiais.cortadas', { mostradas: lista.length, total })}</p>
            <Botao tamanho="sm" variante="fantasma" carregando={carregandoTodas} onClick={() => void verTodas()} data-ver-todas-filiais="">
              {t('crm.filiais.verTodas')}
            </Botao>
          </div>
        )}
      </Cartao>
      </div>
      <PopupVincular
        aberto={etapa === 'popup'}
        aoFechar={fecharPopup}
        titulo={t('crm.filiais.adicionar')}
        rotulo={t('crm.filiais.empresa')}
        acao={t('crm.aj.vincular')}
        busca={{ params: { papel: 'empresa', elegivel_filial: 'true', nao_e: e.id } }}
        cadastrar={t('crm.filiais.cadastrar')}
        aoCadastrar={
          s.pode('cadastros.criar')
            ? () => {
                setEtapa('formulario');
              }
            : undefined
        }
        marca="filial"
        sucesso={t('crm.filiais.vinculadas', { count: 1 })}
        aoVincular={(filial_id) => post(`/cadastros/parceiros/${e.id}/filiais`, { filial_id })}
        lista={{
          pronta: selecionadas.length > 0,
          cheia: selecionadas.length >= limite,
          ocupado: vinculando,
          aoEscolher: (item) => setSelecionadas((x) => comFilial(x, filialDaBusca(item), limite)),
          aoConfirmar: () => void vincular(),
          linhas: (
            <>
              <AvisoPreservadas texto={desistir.aviso} />
              {selecionadas.length >= limite && <AvisoListaCheia limite={limite} />}
              <ListaDeFiliais itens={selecionadas} ocupado={vinculando} aoTirar={(id) =>
                  desistir.perguntarOu(selecionadas.filter((f) => f.id === id), false, () => setSelecionadas((x) => x.filter((f) => f.id !== id)))
                }
                reservas={['[data-cadastrar-filial]', '[role="dialog"] input']}
              />
            </>
          ),
        }}
      />
      {desistir.elemento}
      <FormEmpresa
        aberto={etapa === 'formulario'}
        filial={herancaDaMatriz(responsaveisDoRegistro(e), [...(e.setor_id ? [e.setor_id] : []), ...(e.setores_adicionais ?? []).map((x) => x.id)], selecionadas.length, limite)}
        aoFechar={() => {
          // Sem salvar nesta abertura: o foco volta ao Cadastrar filial, ou ao aviso se a lista encheu.
          focoDepois.current ??= () => document.querySelector<HTMLElement>('[data-cadastrar-filial], [data-lista-cheia]')?.focus();
          setEtapa('popup');
        }}
        aoSalvar={(id, r) => {
          focoDepois.current = () => document.querySelector<HTMLElement>(`[data-tirar-da-lista="${id}"]`)?.focus();
          // Sem teto aqui: o formulário só grava com espaço, e a empresa gravada não some da lista se o teto baixou nesse meio tempo (o servidor recusa o excesso, com o motivo).
          setSelecionadas((x) => comFilial(x, { id, nome: r.nome, documento: r.documento, criada: true }, Infinity));
        }}
      />
      <Confirmar
        aberto={!!tirar}
        aoFechar={() => setTirar(null)}
        titulo={t('crm.filiais.desvincular')}
        texto={t('crm.filiais.desvincularTexto', { nome: nomePessoa(tirar?.nome) })}
        carregando={ocupado}
        aoConfirmar={() => void confirmarTirar()}
      />
    </>
  );
}

/** O cartão Matriz da filial: abrir e desvincular. A matriz arquivada segue no cartão, marcada como inativa, e o vínculo não se desfaz sozinho. */
export function CartaoMatriz({ e }: { e: EmpresaDaFicha }) {
  const { t } = useTranslation('cadastros');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [tirar, setTirar] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const m = e.matriz;
  if (!m) return null;
  const confirmar = async () => {
    setOcupado(true);
    try {
      await del(`/cadastros/parceiros/${m.id}/filiais/${e.id}`);
      setTirar(false);
      await invalidarVinculos(qc);
      toast.sucesso(t('crm.filiais.desvinculada'));
      // Rodada rcn-6 (VIS-B2): o cartão Matriz some; o foco vai à ação do cartão Filiais, que aparece no lugar.
      recuperarFoco({ reservas: RESERVAS_DO_FOCO });
    } catch (err) {
      setTirar(false);
      toast.erro(mensagemDeErro(err));
    } finally {
      setOcupado(false);
    }
  };
  return (
    <>
      <Cartao titulo={t('crm.filiais.matriz')}>
        <div className="flex min-h-11 items-center gap-2 px-2 text-sm" data-matriz-empresa={m.id}>
          <Link to={`${CAMINHO_EMPRESAS}/${m.id}`} className="min-w-0 flex-1 truncate font-semibold text-azul hover:underline" title={nomePessoa(m.nome)}>
            {nomePessoa(m.nome)}
          </Link>
          {m.arquivada && <Selo cor="dourado">{t('crm.inativar.selo')}</Selo>}
          {s.pode('cadastros.editar') && (
            <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.filiais.desvincularDaMatriz', { nome: nomePessoa(m.nome) })} title={t('crm.filiais.desvincular')} icone={<X className="size-4" />} onClick={() => setTirar(true)} data-desvincular-matriz="" />
          )}
        </div>
      </Cartao>
      <Confirmar aberto={tirar} aoFechar={() => setTirar(false)} titulo={t('crm.filiais.desvincular')} texto={t('crm.filiais.desvincularMatrizTexto', { nome: nomePessoa(m.nome) })} carregando={ocupado} aoConfirmar={() => void confirmar()} />
    </>
  );
}
